import { RUNTIME } from '../../config/runtime'
import type { CompetitionState, Quote } from '../../core/types'
import type { DataSource, StateStore } from '../DataSource'
import { createPriceFeed, type PriceFeed } from '../live/priceFeed'
import { Simulator } from './simulator'

/**
 * Runs the round.
 *
 * Two phases. On `start()` the board only connects the price rail and sits in
 * standby: no trades, no positions, no equity, because none of that has
 * happened yet. `activate()` opens the round at that instant and it fills in
 * from empty as it runs.
 *
 * Prices are live in both phases. SOL, ETH and the traded Base tokens are
 * marked by the exchange rather than the RNG, so the rail, the tape and every
 * open position quote the same number.
 */
export class MockDataSource implements DataSource {
  readonly id = 'mock' as const
  private sim = new Simulator(RUNTIME.seed)
  private feed: PriceFeed | null = null
  private timer: number | null = null
  private last = 0
  private running = false
  private marks: Record<string, number> = {}
  private quotes: Quote[] = []
  private priceSource = ''

  constructor(private store: StateStore) {}

  start() {
    if (this.feed) return
    this.feed = createPriceFeed()
    if (!this.feed) {
      this.store.setState({ meta: { ...this.store.getState().meta, status: 'offline' } })
      return
    }
    this.store.setState({ meta: { ...this.store.getState().meta, status: 'connecting' } })
    this.feed.start((u) => {
      this.marks = { ...this.marks, ...u.marks }
      if (u.quotes.length) this.quotes = u.quotes
      this.priceSource = u.source
      if (this.running) {
        for (const [symbol, price] of Object.entries(u.marks)) this.sim.setMark(symbol, price)
      }
      this.store.setState({ quotes: this.quotes, meta: this.meta() })
    })
  }

  /** Open the round, anchored to whatever real prices have arrived so far. */
  activate() {
    if (this.running) return
    this.running = true
    const now = Date.now()
    this.last = now
    this.publish(this.sim.open(now, this.marks))
    this.timer = window.setInterval(() => {
      const t = Date.now()
      const dt = t - this.last
      this.last = t
      this.publish(this.sim.step(t, dt))
    }, RUNTIME.tickMs)
  }

  stop() {
    if (this.timer !== null) window.clearInterval(this.timer)
    this.timer = null
    this.feed?.stop()
    this.feed = null
    this.running = false
  }

  private meta(): CompetitionState['meta'] {
    const s = this.store.getState().meta
    const live = this.quotes.length > 0
    return {
      ...s,
      status: live ? 'live' : 'connecting',
      prices: live ? 'live' : 'offline',
      priceSource: live ? this.priceSource : '',
    }
  }

  private publish(state: CompetitionState) {
    this.store.setState({
      ...state,
      // The rail shows real prices or nothing at all.
      quotes: this.quotes,
      meta: { ...state.meta, ...this.meta(), phase: 'running' },
    })
  }
}
